import {Template} from "../../../components/template/template.jsx";
import React, {useEffect, useMemo, useRef, useState} from "react";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";
import {Card} from "../../../components/card/card.jsx";
import Content from "./components/content.jsx";
import Filters from "./components/filters.jsx";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {useMutation} from "@tanstack/react-query";
import ReportUtils from "../../../utils/report-utils.js";
import reportUtils from "../../../utils/report-utils.js";
import projectUtils from "../../../utils/project-utils.js";
import {subDays} from "date-fns";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import auditService from "../../../services/reports/audit-service.js";
import {appType, appTypeTranslate} from "./utils/audit-utils.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import {useSearchParams} from "react-router";

const initialFilters = () => {
  const refDate = new Date();
  return {
    dtStart: subDays(refDate, 1),
    dtEnd: refDate,
    searchDetails: false,
    application: "all",
    userLogin: null,
    userName: null,
    userCpf: null
  }
}

const filterLabels = {
  dtStart: "Data/hora inicial",
  dtEnd: "Data/hora final",
  userLogin: "Login",
  userName: "Nome",
  userCpf: "CPF",
  searchDetails: "Exibir detalhes",
  application: "Aplicação",
};

const initialTableColumns = {
  user_name: {label: "Agente", visible: true},
  date_time: {label: "Data/hora", visible: true},
  action: {label: "Ação", visible: true},
  application: {label: "Aplicação", visible: true},
};

const Audit = () => {
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const buttonRef = useRef(null);

  const [auditFilters, setAuditFilters] = useState(null);
  const [searchDetails, setSearchDetails] = useState(false);
  const [fixedTop, setFixedTop] = useState(null);
  const [filters, setFilters] = useState(initialFilters());

  const [reportData, setReportData] = useState(null);
  const [title] = useState("Auditoria");

  const [headerSelected, setHeaderSelected] = useState(-1);
  const [tableColumns, setTableColumns] = useState(initialTableColumns);

  const printComponentRef = useRef(null);
  const [searchParams] = useSearchParams();
  const userHistory = searchParams.get("userHistory");
  const isUserHistory = Boolean(userHistory);

  const toggleFilters = () => {
    if (!collapsed && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setFixedTop(rect.top);
    }

    setCollapsed(prev => !prev);
  }

  useEffect(() => {
    if (!reportData) return;

    if (isUserHistory) {
      ReportUtils.appendFilters(auditFilters);
      return;
    }

    ReportUtils.appendFilters(
      ReportUtils.filters({
        ...filters,
        application: appTypeTranslate[filters.application],
        dtStart: filters.dtStart ? ReportUtils.formatDateOnly(filters.dtStart) : "",
        dtEnd: filters.dtEnd ? ReportUtils.formatDateOnly(filters.dtEnd) : "",
      }, filterLabels)
    );

  }, [reportData, isUserHistory, auditFilters]);


  const mutation = useMutation({
    mutationFn: (payload) => auditService.apply(payload),
    onSuccess: data => {
      if (isUserHistory) {
        setAuditFilters(data.filters);
        setReportData(data.data);
        return;
      }

      setReportData(data);
    },
    onError: err => {
      projectUtils.swalErrorMessage("", "Erro ao gerar o relatório.");
    }
  })

  const apply = async () => {
    if (!isUserHistory) {
      const isNotValidDt = reportUtils.periodValidate(filters.dtStart, filters.dtEnd, 1, 'day');
      if (!isNotValidDt.ok) {
        projectUtils.swalErrorMessage("", isNotValidDt.message, "Ok");
        return;
      }
    }

    ReportUtils.applyLoad({
      setLoading,
      clearContent: () => {
        setReportData(null);
      }
    });

    try {
      if (isUserHistory) {
        mutation.mutate({
          userHistory,
          application: "all",
          searchDetails: "on"
        });
        setSearchDetails(true);
        return;
      }

      const formattedFilters = {
        dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
        dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
        searchDetails: filters.searchDetails ? "Sim" : "Não",
        application: appType.find(el => el.value === filters.application).label,
        userLogin: filters.userLogin ? filters.userLogin.label : null,
        userName: filters.userName ? filters.userName.label : null,
        userCpf: filters.userCpf ? filters.userCpf.label : null,
      };

      let filtersToAudit = null;
      if (filters.application && filters.application === "all"){
        const {application, ...rest} = filters;
        filtersToAudit = rest
      }

      const appliedFilters = filtersToAudit ? filtersToAudit : {...filters, application: appType.find(el => el.value === filters.application).label};

      const auditFilters = ReportUtils.filters({
          ...appliedFilters,
          dtStart: formattedFilters.dtStart,
          dtEnd: formattedFilters.dtEnd,
          searchDetails: formattedFilters.searchDetails,
        },
        filterLabels, true);

      setAuditFilters(formattedFilters);

      mutation.mutate({
        ...buildPayload(filters),
        filters: auditFilters
      });

      setSearchDetails(filters.searchDetails);

      const scale = window.visualViewport?.scale ?? 1;
      if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

    } catch (error) {

      ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
      console.error("Erro ao gerar o relatório", error);

    } finally {
      setLoading(false);
    }
  }

  const clearSearch = () => {
    setFilters({...initialFilters()});
    setReportData(null);
  }

  function buildPayload(data){
    const payloadObj = {
      date_start: reportUtils.formatDateTime(data.dtStart),
      date_end: reportUtils.formatDateTime(data.dtEnd),
      application: data.application
    }

    if (data.userCpf) payloadObj.userCpf = data.userCpf.value;
    if (data.userName) payloadObj.userName = data.userName.value;
    if (data.userLogin) payloadObj.userLogin = data.userLogin.value;
    if (data.searchDetails) payloadObj.searchDetails = "on";

    return payloadObj
  }

  useGlobalKey("Enter", apply);
  useGlobalKey("Escape", clearSearch);

  useEffect(() => {
    if (isUserHistory) apply();
  }, []);

  useMemo(() => {
    setTableColumns(initialTableColumns);
  }, [searchDetails]);

  return(
    <SubScroll dependency={[collapsed, reportData]}>
      <div className={"flex"}>

        <Template isLoading={loading || mutation.isPending}>
          <Template.Header>
            <div className="flex justify-between w-full">
              <Template.Title>{title}</Template.Title>
              <HeaderButtons
                exportable={!searchDetails}
                exportInSheets={!searchDetails}
                printable={true}
                headerChange={true}
                visibilityColumns={!searchDetails}
                columns={tableColumns}
                setColumns={setTableColumns}
                printComponentRef={printComponentRef}
                reportData={reportData}
                data={reportData?.length}
                headerSelected={headerSelected}
                setHeaderSelected={setHeaderSelected}
              />
            </div>
          </Template.Header>
          <div className={isUserHistory ? "hidden" : ""}>
          <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
            <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                  collapsed={collapsed} apply={apply} clearSearch={clearSearch}>
              <Filters setFilters={setFilters} filters={filters}/>
              <AutoDatetime setFilters={setFilters}/>
            </Menu>
          </Template.Body>
          </div>
        </Template>

        <Card className={`w-full h-fit mr-2 ${!reportData || reportData?.length === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || reportData?.length === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
          <Card.Body>
            <div className={`flex-1 ${!reportData || reportData?.length === 0 ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
              <NotAppliedContent reportData={reportData}/>
              <NoDataContent reportData={reportData} data={reportData?.length}/>
              <Content reportData={reportData} data={reportData?.length} entity={headerSelected} title={title} setLoading={setLoading} printComponentRef={printComponentRef} searchDetails={searchDetails} filters={auditFilters} tableColumns={tableColumns}/>
            </div>
          </Card.Body>
        </Card>

      </div>
    </SubScroll>
  )
}

export default Audit;